import { useLanguage } from "../contexts/LanguageContext";
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../Components/ui/card';
import { Button } from '../Components/ui/button';
import { Input } from '../Components/ui/input';
import { Textarea } from '../Components/ui/textarea';
import { Star, CheckCircle, Loader2 } from 'lucide-react';
import SEO from '../Components/SEO';

const API_BASE_URL = 'http://localhost:8000';

const MessageBoard = () => {
  const { tx } = useLanguage();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [newComment, setNewComment] = useState({
    name: '',
    email: '',
    content: '',
    rating: 5
  });
  const [errors, setErrors] = useState({});

  // Validate form
  const validateForm = (formData) => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'Please select a rating between 1 and 5';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle message submission
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!validateForm(newComment)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/message-board/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newComment),
      });

      if (response.ok) {
        await response.json();
        setIsSubmitted(true);
        // Reset form
        setNewComment({
          name: '',
          email: '',
          content: '',
          rating: 5
        });
        setErrors({});
        // Redirect to thank you page after 1.5 seconds
        setTimeout(() => {
          navigate('/thank-you');
          // Ensure page scrolls to top after navigation
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }, 1500);
      } else {
        const errorData = await response.json();
        alert(tx(errorData.error || 'Failed to send message'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert(tx('Failed to send message. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewComment(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for corresponding field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle rating changes
  const handleRatingChange = (rating) => {
    setNewComment(prev => ({
      ...prev,
      rating: rating
    }));
    
    if (errors.rating) {
      setErrors(prev => ({
        ...prev,
        rating: ''
      }));
    }
  };

  // Render star rating
  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={interactive && onChange ? () => onChange(star) : undefined}
            className={`${
              interactive ? 'cursor-pointer hover:scale-125 active:scale-95' : 'cursor-default'
            } transition-all duration-200 p-1 rounded-lg hover:bg-yellow-100`}
          >
            <Star
              className={`w-8 h-8 ${
                star <= rating
                  ? 'text-yellow-400 fill-current drop-shadow-sm'
                  : 'text-gray-300 hover:text-yellow-200'
              } transition-colors duration-200`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (isSubmitted) {
    return (
      <>
        <SEO noindex={true} nofollow={true} />
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-indigo-50 flex items-center justify-center py-12">
        <div className="max-w-md mx-auto px-4">
          <Card className="p-8 text-center shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-blue-500 rounded-full blur-xl opacity-20 scale-150"></div>
              <CheckCircle className="relative w-20 h-20 text-emerald-500 mx-auto mb-6 animate-pulse" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent mb-3">{tx("Message Sent Successfully!")}</h1>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">{tx("Thank you for your feedback. We have received your message and will get back to you soon.")}</p>
            <Button
              onClick={() => setIsSubmitted(false)}
              className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >{tx("Send Another Message")}</Button>
          </Card>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO noindex={true} nofollow={true} />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-blue-500 rounded-2xl blur-xl opacity-20 scale-110"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-white/20">
              <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-4">{tx("Message Board")}</h1>
              <p className="text-xl text-gray-600 leading-relaxed">{tx("Share your thoughts with us and help us improve")}</p>
            </div>
          </div>
        </div>

        {/* Message Form */}
        <Card className="p-10 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent mb-2">{tx("Leave a Message")}</h2>
            <p className="text-gray-600">{tx("We'd love to hear from you")}</p>
          </div>
          <form onSubmit={handleSubmitComment} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>{tx("Name *")}{" "}</span>
                </label>
                <Input
                  type="text"
                  name="name"
                  value={newComment.name}
                  onChange={handleInputChange}
                  placeholder={tx("Enter your name")}
                  className={`h-12 rounded-xl border-2 transition-all duration-300 focus:ring-4 focus:ring-emerald-100 ${
                    errors.name 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-gray-200 focus:border-emerald-500 hover:border-emerald-300'
                  }`}
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-500 flex items-center">
                    <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                    {tx(errors.name)}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>{tx("Email *")}{" "}</span>
                </label>
                <Input
                  type="email"
                  name="email"
                  value={newComment.email}
                  onChange={handleInputChange}
                  placeholder={tx("Enter your email")}
                  className={`h-12 rounded-xl border-2 transition-all duration-300 focus:ring-4 focus:ring-blue-100 ${
                    errors.email 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-gray-200 focus:border-blue-500 hover:border-blue-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-500 flex items-center">
                    <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                    {tx(errors.email)}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>{tx("Rating *")}{" "}</span>
              </label>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  {renderStars(newComment.rating, true, handleRatingChange)}
                </div>
                <div className="text-center">
                  <span className="inline-flex items-center px-4 py-2 bg-white rounded-full text-sm font-semibold text-gray-700 shadow-sm border border-yellow-200">
                    {tx(newComment.rating === 1 ? '{count} star' : '{count} stars', { count: newComment.rating })}
                  </span>
                </div>
              </div>
              {errors.rating && (
                <p className="mt-2 text-sm text-red-500 flex items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                  {tx(errors.rating)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>{tx("Message *")}{" "}</span>
              </label>
              <Textarea
                name="content"
                value={newComment.content}
                onChange={handleInputChange}
                placeholder={tx("Share your thoughts with us...")}
                rows={5}
                className={`rounded-xl border-2 transition-all duration-300 focus:ring-4 focus:ring-purple-100 resize-none ${
                  errors.content 
                    ? 'border-red-400 focus:border-red-500' 
                    : 'border-gray-200 focus:border-purple-500 hover:border-purple-300'
                }`}
              />
              {errors.content && (
                <p className="mt-2 text-sm text-red-500 flex items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                  {tx(errors.content)}
                </p>
              )}
            </div>

            <div className="pt-6">
              <Button
                type="submit"
                disabled={isSubmitting || isSubmitted}
                className="w-full h-14 bg-gradient-to-r from-emerald-600 via-blue-600 to-indigo-600 hover:from-emerald-700 hover:via-blue-700 hover:to-indigo-700 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />{tx("Sending...")}{" "}</div>
                ) : isSubmitted ? (
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />{tx("Sent Successfully!")}{" "}</div>
                ) : (
                  tx('Send Message')
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
    </>
  );
};

export default MessageBoard;
